import { useCallback } from 'react';
import { mapSearchListingResultToPropertyContext, mapSearchStateToAiSearchContext } from '@pkgs/components/utils/tracking';
import { getConsentContext, useSearchModuleTracking } from '@pkgs/components/hooks/useSearchModuleTracking';
import {
  getAiSearchContext,
  getNavigationContext,
  getPageContext,
  getPropertyContext,
  trackNavigationEvent,
  trackUserInteractionEvent,
  UserActionEnum,
} from '@ev-tech/snowplow-library';
import { type MultiPolygonG2D, type SearchListingResult } from '@ev-tech/search-modules-api';

import { useAppContext } from './useAppContext';
import { useSearchStore } from './stores/search/useSearchStore';
import { SearchMode } from './stores/search/slices/searchModeSlice';
import { mapSearchStateToSearchListingsFilters } from '../utils/mapFilterFields';
import { createSerpUrl } from '../utils/createSerpUrl';
import { PageType, TemplateType } from '../types/tracking';

export const useMapTracking = () => {
  const { pageContext, shopContext, mapABTestContext } = useSearchModuleTracking();
  const {
    countryCode,
    options: { language },
  } = useAppContext();
  const getStateSnapshot = useSearchStore((state) => state.getStateSnapshot);
  const placeName = useSearchStore((state) => state.placeName);

  const trackPropertyMarkerClick = useCallback(
    (propertyId: string, state: 'open' | 'close') => {
      trackUserInteractionEvent({
        elementId: `map-module_property-pin_${state}`,
        action: UserActionEnum.click,
        contexts: [
          pageContext,
          shopContext,
          getConsentContext(),
          getPropertyContext({
            bffObjectId: propertyId,
          }),
          mapABTestContext(true),
        ],
      });
    },
    [pageContext, shopContext, mapABTestContext],
  );

  const trackClusterClick = useCallback(
    (state: 'open' | 'close' | 'click', depthIndex?: number) => {
      trackUserInteractionEvent({
        elementId: `map-module_property-cluster${depthIndex ? `-${depthIndex}` : ''}_${state}`,
        action: UserActionEnum.click,
        contexts: [pageContext, shopContext, getConsentContext(), mapABTestContext(true)],
      });
    },
    [pageContext, shopContext, mapABTestContext],
  );

  const trackMapToggleClick = useCallback(
    (state: 'on' | 'off') => {
      trackUserInteractionEvent({
        elementId: `map-module_${state}`,
        action: UserActionEnum.click,
        contexts: [pageContext, shopContext, getConsentContext(), mapABTestContext(state === 'on')],
      });
    },
    [pageContext, shopContext, mapABTestContext],
  );

  const trackDrawOnMapUserInteractionEvents = useCallback(
    (elementId: string) => {
      trackUserInteractionEvent({
        elementId,
        action: UserActionEnum.click,
        contexts: [pageContext, shopContext, getConsentContext(), mapABTestContext(true)],
      });
    },
    [pageContext, shopContext, mapABTestContext],
  );

  const trackSearchThisAreaClick = useCallback(
    (multiPolygon: MultiPolygonG2D) => {
      const { href } = createSerpUrl({
        filters: {
          ...mapSearchStateToSearchListingsFilters(getStateSnapshot()),
          multiPolygon,
          placeIds: undefined,
        },
        options: { language, countryCode },
        additionalParams: { placeName },
      });
      trackNavigationEvent({
        screenName: '',
        contexts: [
          pageContext,
          shopContext,
          getConsentContext(),
          getNavigationContext({
            target_url: href,
            element_id: 'map-module_area-site-search',
          }),
          mapABTestContext(true),
        ],
      });
    },
    [getStateSnapshot, placeName, language, countryCode, pageContext, shopContext, mapABTestContext],
  );

  const trackPreviewCardClick = useCallback(
    (exposeUrl: string, listingResult: SearchListingResult, searchMode: SearchMode) => {
      const propertyContext = mapSearchListingResultToPropertyContext(listingResult);

      const isDevelopmentProject = Boolean(listingResult.listing?.isDevelopmentProject);

      const pageContext = getPageContext({
        type: isDevelopmentProject ? PageType.DEVELOPMENT_EXPOSE : PageType.EXPOSE,
        isHQPage: true,
        template_type: TemplateType.EXPOSE,
        page_language: language,
        country_code: countryCode,
      });

      trackNavigationEvent({
        screenName: '',
        contexts: [
          pageContext,
          shopContext,
          getConsentContext(),
          getPropertyContext(propertyContext),
          getNavigationContext({
            target_url: exposeUrl,
            element_id: 'map-module_property-preview_link',
          }),
          mapABTestContext(true),
          getAiSearchContext(
            mapSearchStateToAiSearchContext({
              preferences: getStateSnapshot().preferences,
              isAiSearchEnabled: searchMode === SearchMode.AI,
              preferenceScoreOverall: listingResult.listing?.preferenceScoreOverall,
            }),
          ),
        ],
      });
    },
    [language, countryCode, shopContext, mapABTestContext, getStateSnapshot],
  );

  return {
    trackPropertyMarkerClick,
    trackClusterClick,
    trackMapToggleClick,
    trackDrawOnMapUserInteractionEvents,
    trackSearchThisAreaClick,
    trackPreviewCardClick,
  };
};
